import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { ChartsReferenceLine } from '@mui/x-charts/ChartsReferenceLine';
import { DistanceValueUnit } from '@common/enums/configuration/units';
import useDistanceUnitValue from '@common/hooks/useDistanceUnitValue';
import { MissionCodeType } from 'models/enums/fleet-management';
import {
  FleetDataPerformanceChange,
  RobotPerformanceAnswer,
} from 'models/fleet-management/FleetDataSet';
import { InsightsQuery } from 'store/fleet-management/fleet-management-actions';
import { useFleetInsights } from 'hooks/useFleetDataset';
import useCodeLabel from 'hooks/useCodeLabel';
import openRobotPage from 'utils/open-robot-page';
import {
  CHART_AXIS,
  CHART_SERIES_BLUE,
  CHART_TICK_LABEL,
  CODE_TONES,
  FLEET_SECTION_GAP,
  HEALTH_STATUS_LABEL_KEYS,
  HEALTH_TONES,
  PERFORMANCE_TONE,
  chartSx,
} from 'utils/fleet-dashboard-theme';
import { fillRobotWeeks, median, versionChanges, weekKey } from 'utils/performance-changes';
import DialogContent from '@mui/material/DialogContent';
import DialogWrapperWithClose from '@common/components/General/DialogWrapperWithClose';
import { CodeTooltipContent } from './CodeChips';
import DatasetState from './DatasetState';
import { MeterListSkeleton } from './SectionSkeletons';
import { METER_TRACK } from './MeterTrack';

const CHART_HEIGHT = 220;
const CHART_MARGIN = { left: 56, right: 16, top: 24, bottom: 32 };
const CODE_STRIP_LIMIT = 8;

/** Light to dark by how many missions reported the code that week: one, two, three or more. */
const CODE_RAMPS: Record<MissionCodeType, string[]> = {
  [MissionCodeType.ERROR]: ['#F3A9A6', '#E0433E', '#B3201C'],
  [MissionCodeType.MAINTENANCE]: ['#F6D38A', '#E8A317', '#8A5203'],
};

const referenceLineSx = { strokeDasharray: '5 4', stroke: CHART_AXIS, strokeWidth: 1.5 };
const referenceLabelStyle = { fontSize: 11, fill: CHART_TICK_LABEL };

/**
 * One robot's last twelve weeks: what it cleaned, how fast, where its software changed and which
 * codes it reported, so a drop in the ranking can be traced to a cause.
 */
const RobotPerformanceDetail: React.FC<{
  robot: FleetDataPerformanceChange;
  /** The page's filters over the trend window. */
  query: InsightsQuery;
  grid: string[];
  onClose: () => void;
}> = ({ robot, query, grid, onClose }) => {
  const { t, i18n } = useTranslation();
  const codeLabel = useCodeLabel();
  const { multiplier, label: areaUnit } = useDistanceUnitValue(1, DistanceValueUnit.METERS_SQUARED);
  const { label: performanceUnit } = useDistanceUnitValue(
    1,
    DistanceValueUnit.METERS_SQUARED_PER_HOUR,
  );

  const answer = useFleetInsights<RobotPerformanceAnswer>(`${robot.id}/robot-performance`, query);
  const weeksDataset = { ...answer, data: answer.data?.weeks ?? null };
  const codesDataset = { ...answer, data: answer.data?.codes ?? null };

  const weekLabel = (week: string) =>
    DateTime.fromISO(week).setLocale(i18n.language).toFormat('dd LLL');

  const weeks = useMemo(
    () =>
      fillRobotWeeks(weeksDataset.data ?? [], grid).map((week) => ({
        ...week,
        label: weekLabel(week.week),
        area: week.cleanedArea * multiplier,
        performance:
          week.cleaningPerformance == null ? null : week.cleaningPerformance * multiplier,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [grid, i18n.language, multiplier, weeksDataset.data],
  );
  const updates = useMemo(() => versionChanges(weeks), [weeks]);
  // The charts take scalars only, so the version lists stay behind.
  const chartWeeks = useMemo(
    () => weeks.map(({ label, area, performance }) => ({ label, area, performance })),
    [weeks],
  );
  const areaMedian = useMemo(
    () => median(weeks.filter((week) => week.missionCount > 0).map((week) => week.area)),
    [weeks],
  );
  const lastWeek = weeks.find((week) => week.week === weekKey(robot.week_start));

  const codeStrips = useMemo(() => {
    const byCode = new Map<
      string,
      { code: string; type: MissionCodeType; total: number; weeks: Map<string, number> }
    >();
    (codesDataset.data ?? []).forEach((row) => {
      const key = `${row.code_type}:${row.code}`;
      const entry = byCode.get(key) ?? {
        code: row.code,
        type: row.code_type,
        total: 0,
        weeks: new Map<string, number>(),
      };
      const week = weekKey(row.week_start);
      entry.total += row.occurrences;
      entry.weeks.set(week, (entry.weeks.get(week) ?? 0) + row.occurrences);
      byCode.set(key, entry);
    });
    return [...byCode.values()].sort((a, b) => b.total - a.total).slice(0, CODE_STRIP_LIMIT);
  }, [codesDataset.data]);

  const area = (value: number) =>
    `${Math.round(value * multiplier).toLocaleString(i18n.language)} ${areaUnit}`;

  const health = HEALTH_TONES[robot.customer_journey_status];

  const stats = [
    {
      key: 'last-week',
      label: t('fleet-management-detail-last-week'),
      value: area(robot.last_week_area),
    },
    {
      key: 'baseline',
      label: t('fleet-management-detail-baseline'),
      value:
        robot.baseline_weekly_area == null
          ? t('fleet-management-no-baseline')
          : area(robot.baseline_weekly_area),
    },
    {
      key: 'performance',
      label: t('fleet-management-detail-performance'),
      value:
        lastWeek?.performance == null
          ? '—'
          : `${Math.round(lastWeek.performance).toLocaleString(i18n.language)} ${performanceUnit}`,
    },
    {
      key: 'missions',
      label: t('fleet-management-detail-missions-last-week'),
      value: robot.mission_count.toLocaleString(i18n.language),
    },
  ];

  const updateLines = updates.map((update) => (
    <ChartsReferenceLine
      key={update.week}
      x={weekLabel(update.week)}
      label={update.version ? `RSW ${update.version}` : undefined}
      labelAlign="start"
      lineStyle={{ ...referenceLineSx, stroke: PERFORMANCE_TONE.mark }}
      labelStyle={{ ...referenceLabelStyle, fill: PERFORMANCE_TONE.ink }}
    />
  ));

  const detailState = {
    isLoading: weeksDataset.isLoading,
    hasError: weeksDataset.hasError,
    hasData: weeksDataset.data != null,
    skeletonHeight: CHART_HEIGHT,
  };

  return (
    <DialogWrapperWithClose
      open
      onClose={onClose}
      title={robot.customer ? `${robot.name} · ${robot.customer}` : robot.name}
      fullWidth
      maxWidth="lg"
    >
      <DialogContent>
        <Stack gap={FLEET_SECTION_GAP} role="region" aria-label={robot.name} pt={1}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            gap={1}
          >
            <Typography variant="body2" color="text.secondary">
              {[
                robot.dealer,
                robot.model,
                robot.hardware_version__robot_generation,
                robot.software_version__rsw_software_version &&
                  `RSW ${robot.software_version__rsw_software_version}`,
              ]
                .filter(Boolean)
                .join(' · ')}
            </Typography>
            <Stack direction="row" gap={1} alignItems="center">
              <Chip
                size="small"
                label={t(HEALTH_STATUS_LABEL_KEYS[robot.customer_journey_status])}
                sx={{ fontWeight: 600, color: health.ink, backgroundColor: health.tint }}
              />
              <Button size="small" variant="outlined" onClick={() => openRobotPage(robot.id)}>
                {t('button-open')}
              </Button>
            </Stack>
          </Stack>

          <Grid container spacing={2}>
            {stats.map((stat) => (
              <Grid item xs={6} md={3} key={stat.key}>
                <Stack gap={0.5}>
                  <Typography variant="caption" color="text.secondary">
                    {stat.label}
                  </Typography>
                  <Typography variant="h6" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {stat.value}
                  </Typography>
                </Stack>
              </Grid>
            ))}
          </Grid>

          <Stack direction={{ xs: 'column', lg: 'row' }} gap={FLEET_SECTION_GAP}>
            <Stack flex="1 1 0" minWidth={0} gap={1}>
              <Typography variant="subtitle2">
                {t('fleet-management-detail-area-per-week', { unit: areaUnit })}
              </Typography>
              <DatasetState {...detailState}>
                <BarChart
                  dataset={chartWeeks}
                  xAxis={[{ dataKey: 'label', scaleType: 'band' }]}
                  yAxis={[
                    { valueFormatter: (v: number) => Math.round(v).toLocaleString(i18n.language) },
                  ]}
                  series={[
                    {
                      dataKey: 'area',
                      label: t('fleet-management-detail-area-per-week', { unit: areaUnit }),
                      color: CHART_SERIES_BLUE,
                      valueFormatter: (v) =>
                        `${Math.round(v ?? 0).toLocaleString(i18n.language)} ${areaUnit}`,
                    },
                  ]}
                  height={CHART_HEIGHT}
                  margin={CHART_MARGIN}
                  grid={{ horizontal: true }}
                  borderRadius={4}
                  slotProps={{ legend: { hidden: true } }}
                  sx={chartSx}
                >
                  {areaMedian != null && (
                    <ChartsReferenceLine
                      y={areaMedian}
                      label={t('fleet-management-detail-median')}
                      labelAlign="end"
                      lineStyle={referenceLineSx}
                      labelStyle={referenceLabelStyle}
                    />
                  )}
                  {updateLines}
                </BarChart>
              </DatasetState>
            </Stack>
            <Stack flex="1 1 0" minWidth={0} gap={1}>
              <Typography variant="subtitle2">
                {t('fleet-management-detail-performance-per-week', { unit: performanceUnit })}
              </Typography>
              <DatasetState {...detailState}>
                <LineChart
                  dataset={chartWeeks}
                  xAxis={[{ dataKey: 'label', scaleType: 'point' }]}
                  series={[
                    {
                      dataKey: 'performance',
                      label: t('fleet-management-detail-performance-per-week', {
                        unit: performanceUnit,
                      }),
                      color: PERFORMANCE_TONE.mark,
                      showMark: true,
                      valueFormatter: (v) =>
                        v == null
                          ? '—'
                          : `${Math.round(v).toLocaleString(i18n.language)} ${performanceUnit}`,
                    },
                  ]}
                  height={CHART_HEIGHT}
                  margin={CHART_MARGIN}
                  grid={{ horizontal: true }}
                  slotProps={{ legend: { hidden: true } }}
                  sx={chartSx}
                >
                  {updateLines}
                </LineChart>
              </DatasetState>
            </Stack>
          </Stack>

          <Stack gap={1.25}>
            <Typography variant="subtitle2">{t('fleet-management-detail-codes')}</Typography>
            <DatasetState
              isLoading={codesDataset.isLoading}
              hasError={codesDataset.hasError}
              hasData={codesDataset.data != null}
              skeleton={<MeterListSkeleton rows={3} gap={1} />}
            >
              <Stack gap={1}>
                {codeStrips.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    {t('fleet-management-no-codes')}
                  </Typography>
                )}
                {codeStrips.map((strip) => {
                  const ramp = CODE_RAMPS[strip.type];
                  return (
                    <Stack
                      key={`${strip.type}:${strip.code}`}
                      direction="row"
                      alignItems="center"
                      gap={1.5}
                    >
                      <Tooltip title={<CodeTooltipContent code={strip.code} type={strip.type} />}>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          noWrap
                          color={CODE_TONES[strip.type].ink}
                          sx={{ width: { xs: 96, sm: 240 }, flexShrink: 0 }}
                        >
                          {codeLabel(strip.code, strip.type).title}
                        </Typography>
                      </Tooltip>
                      <Stack direction="row" gap="3px" flex={1} minWidth={0}>
                        {grid.map((week) => {
                          const count = strip.weeks.get(week) ?? 0;
                          return (
                            <Box
                              key={week}
                              data-testid={`code-cell-${strip.code}`}
                              data-count={count}
                              title={`${weekLabel(week)}: ${count}`}
                              sx={{
                                flex: '1 1 0',
                                height: 16,
                                borderRadius: '3px',
                                backgroundColor:
                                  count === 0
                                    ? METER_TRACK
                                    : ramp[Math.min(count, ramp.length) - 1],
                              }}
                            />
                          );
                        })}
                      </Stack>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        width={88}
                        textAlign="right"
                        sx={{ fontVariantNumeric: 'tabular-nums' }}
                      >
                        {t('fleet-management-mission-count', { count: strip.total })}
                      </Typography>
                    </Stack>
                  );
                })}
                <Typography variant="caption" color="text.secondary">
                  {t('fleet-management-detail-codes-caption')}
                </Typography>
              </Stack>
            </DatasetState>
          </Stack>
        </Stack>
      </DialogContent>
    </DialogWrapperWithClose>
  );
};

export default RobotPerformanceDetail;
